import { useCallback } from 'react';
import { useRecoilCallback } from 'recoil';
import { AnnotationRecord } from '~/domain/annotation/annotation';
import { PointMm } from '~/domain/geometry/patientGeometry';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    frameRotationShowingPlane,
    frameShowsPlane,
    imageToPaneFrame,
} from '~/engine/view/planeViewGeometry';
import { useImageForSeries } from '~/hooks/image/useImageForSeries';
import type { Environment } from '~/engine/data/Environment';
import type { ViewManager } from '~/engine/view/ViewManager';
import { localization } from '~/i18n';
import { AnnotationListState } from '~/state/atoms/annotationListAtoms';
import { viewManagerState } from '~/state/atoms/engineAtoms';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * Turns and centres the tiles of an Environment onto an annotation's plane,
 * unless they already show it (a roll within the plane counts as showing
 * it), so a pan the user set is kept. The Environment's frame belongs to the
 * manager (one frame per Environment, one writer), so the turn goes through
 * it and reaches every tile of the row.
 */
function showPlaneOnEnvironment(
    viewManager: ViewManager,
    environment: Environment,
    plane: AnnotationRecord['plane'],
    grid: NonNullable<Environment['image']>['grid']
): void {
    if (
        frameShowsPlane(viewManager.frameRotationOf(environment), plane, grid)
    ) {
        return;
    }
    viewManager.setFrameRotation(
        environment,
        frameRotationShowingPlane(plane, grid)
    );
    // The turn pivots on the crosshair, which has just moved, so each tile is
    // centred on its image afterwards.
    const image = environment.image;
    if (!image) {
        return;
    }
    for (const view of viewManager.viewsOf(environment)) {
        view.centerOn(image.boundingBox);
        view.updateView();
    }
}

/** The warning of a go-to on an annotation whose image no tile shows. */
const IMAGE_NOT_SHOWN_MESSAGE_ID = 'annotation.goTo.imageNotShown';

/** Where a go-to lands: a row, and the anchor and plane in its frame. */
interface GoToTarget {
    readonly environment: Environment;
    readonly grid: NonNullable<Environment['image']>['grid'];
    readonly anchorMm: PointMm;
    readonly plane: AnnotationRecord['plane'];
}

/**
 * The row a go-to on `item` moves: the row of its series when a tile there
 * draws that image, else a row with a tile that draws the series as the
 * fused secondary alone, first in registry order, with the anchor and the
 * plane moved onto that tile through the registration. Undefined when no
 * tile draws the series.
 */
function goToTargetOf(
    viewManager: ViewManager,
    item: AnnotationRecord,
    anchorMm: PointMm
): GoToTarget | undefined {
    const seriesUid = item.plane.seriesInstanceUid;
    const ownRow = viewManager.environmentShowingSeries(seriesUid);
    const ownGrid = ownRow?.image?.grid;
    if (
        ownRow &&
        ownGrid &&
        viewManager
            .viewsOf(ownRow)
            .some((view) => paneImageOf(view).kind === 'primary')
    ) {
        return {
            environment: ownRow,
            grid: ownGrid,
            anchorMm,
            plane: item.plane,
        };
    }
    for (const environment of getDatasetEnvironmentRegistry().residentEnvironments()) {
        const grid = environment.image?.grid;
        if (!grid) {
            continue;
        }
        for (const view of viewManager.viewsOf(environment)) {
            const pane = paneImageOf(view);
            if (pane.kind === 'secondary' && pane.seriesUid === seriesUid) {
                const ontoPane = imageToPaneFrame(pane);
                return {
                    environment,
                    grid,
                    anchorMm: ontoPane.point(anchorMm),
                    plane: ontoPane.plane(item.plane),
                };
            }
        }
    }
    return undefined;
}

/**
 * Moves the crosshair to `anchorMm` and turns its row to `item`'s plane;
 * warns and returns false when no tile draws its image.
 */
export function goToAnchor(
    viewManager: ViewManager | null,
    item: AnnotationRecord,
    anchorMm: PointMm
): boolean {
    const target = viewManager && goToTargetOf(viewManager, item, anchorMm);
    if (!viewManager || !target) {
        enqueueSnackbar(localization.t(IMAGE_NOT_SHOWN_MESSAGE_ID), {
            variant: 'warning',
        });
        return false;
    }
    // Move the crosshair first: it decides which slice each tile shows, and
    // the turn rotates about it.
    target.environment.updatePlaneIntersectionPoint(target.anchorMm);
    showPlaneOnEnvironment(
        viewManager,
        target.environment,
        target.plane,
        target.grid
    );
    return true;
}

export interface AnnotationGoTo {
    /**
     * Shows the annotation as it was placed: scrolls to its slice and turns
     * the tiles to the plane it was placed in, in the row of its image, or
     * else in a row that draws its image as the fused secondary. Warns, and
     * moves nothing, when no tile draws the annotation's image.
     */
    goTo: (id: string) => void;
}

/**
 * Go-to for one kind of placed annotation.
 *
 * `anchorOf` must return a point on the annotation's own slice: moving the
 * crosshair there is what scrolls the tiles to it. Go-to moves nothing unless
 * a tile draws the annotation's image, as its row's image or as the fused
 * secondary, so the crosshair is never moved to coordinates that belong to
 * another image and no row turns for an annotation it does not draw; it says
 * so instead, since a go-to that moves nothing would otherwise read as a
 * lost click.
 *
 * Define `anchorOf` at module level rather than inline, so the returned
 * callbacks keep their identity across renders.
 */
export function useAnnotationGoTo<T extends AnnotationRecord>(
    list: AnnotationListState<T>,
    anchorOf: (item: T) => PointMm
): AnnotationGoTo {
    // Reads the view manager from the snapshot at click time, so the hook
    // does not subscribe to it.
    const goTo = useRecoilCallback(
        ({ snapshot }) =>
            (id: string) => {
                const item = snapshot.getLoadable(list.item(id)).getValue();
                if (!item) {
                    return;
                }
                const viewManager = snapshot
                    .getLoadable(viewManagerState)
                    .getValue();
                goToAnchor(viewManager, item, anchorOf(item));
            },
        [list, anchorOf]
    );

    return { goTo };
}

/**
 * Whether an annotation's plane is not one of its image's own slices: the
 * picture there is interpolated between slices, while the values are read
 * from the voxels. False once the series is no longer in the patient.
 */
export function useIsObliqueToImage(): (
    item: Pick<AnnotationRecord, 'plane'>
) => boolean {
    const imageForSeries = useImageForSeries();
    return useCallback(
        ({ plane }: Pick<AnnotationRecord, 'plane'>) => {
            const grid = imageForSeries(plane.seriesInstanceUid)?.grid;
            return !!grid && grid.axisAlong(plane.viewDirection) === undefined;
        },
        [imageForSeries]
    );
}
